import {PermissonsSectionType, AccessLevelType, RoleType, PermissonDictionaryType} from '../models'
import {InviteTeammatePermissions} from '@/pages/TeamManagement/components/InviteEditTeammate/models'
import {Role} from '../constants'

const ADMIN_SECTIONS = ['teammates']

const PRODUCT_DEPENDENT_SECTIONS: Record<string, string> = {
    pos_amex_payments: 'amex'
}

export function transformPermissionsToConfig(
    roles: RoleType[],
    permissionsDictionaryRaw: PermissonDictionaryType[]
): PermissonsSectionType[] {
    const map = new Map<string, Set<AccessLevelType>>()
    const admin = roles.find((r) => r.role === Role.admin)

    for (const perm of admin.permissions) {
        const lastDotIndex = perm.lastIndexOf('.')
        if (lastDotIndex === -1) continue

        const code = perm.slice(0, lastDotIndex)
        const level = perm.slice(lastDotIndex + 1) as AccessLevelType

        if (!map.has(code)) {
            map.set(code, new Set<AccessLevelType>(['no']))
        }

        map.get(code)!.add(level)
    }

    const order: AccessLevelType[] = ['no', 'read', 'full']

    const uniqueSections = new Set<string>()
    const result: PermissonsSectionType[] = []

    for (const {id, name} of permissionsDictionaryRaw) {
        const lastDotIndex = id.lastIndexOf('.')
        if (lastDotIndex === -1) continue

        const code = id.slice(0, lastDotIndex)

        if (uniqueSections.has(code)) continue
        if (!map.has(code)) continue

        uniqueSections.add(code)

        const originalSet = map.get(code)!
        const levels = new Set<AccessLevelType>(originalSet)

        levels.add('read')

        const accessLevels = order.filter((level) => levels.has(level))

        const section: PermissonsSectionType = {
            name,
            code,
            accessLevels
        }

        if (!originalSet.has('read')) {
            section.readDisabled = true
        }

        if (ADMIN_SECTIONS.includes(code)) {
            section.requiresAdmin = true
        }

        if (PRODUCT_DEPENDENT_SECTIONS[code]) {
            section.requiresProduct = PRODUCT_DEPENDENT_SECTIONS[code]
        }

        result.push(section)
    }

    return result
}

function extractAllSectionsFromAdminRole(roles: RoleType[]): string[] {
    const admin = roles.find((r) => r.role === Role.admin)
    if (!admin) throw new Error('Admin role not found')

    const sections = new Set<string>()

    for (const perm of admin.permissions) {
        const lastDot = perm.lastIndexOf('.')
        if (lastDot === -1) continue

        const code = perm.slice(0, lastDot)
        sections.add(code)
    }

    return Array.from(sections)
}

export function buildPermissionsFromRoles(
    roles: RoleType[],
    targetRole: Role
): InviteTeammatePermissions {
    const allSections = extractAllSectionsFromAdminRole(roles)

    if (targetRole === 'custom') {
        return Object.fromEntries(
            allSections.map((section) => [section, 'no'])
        ) as InviteTeammatePermissions
    }

    const target = roles.find((r) => r.role === targetRole)
    if (!target) throw new Error(`Role "${targetRole}" not found`)

    const levelOrder: AccessLevelType[] = ['no', 'read', 'full']
    const grouped: Record<string, AccessLevelType> = {}

    for (const perm of target.permissions) {
        const lastDot = perm.lastIndexOf('.')
        if (lastDot === -1) continue

        const code = perm.slice(0, lastDot)
        const level = perm.slice(lastDot + 1) as AccessLevelType

        const current = grouped[code]
        if (!current || levelOrder.indexOf(level) > levelOrder.indexOf(current)) {
            grouped[code] = level
        }
    }

    const result: InviteTeammatePermissions = {} as InviteTeammatePermissions

    for (const section of allSections) {
        result[section] = grouped[section] || 'no'
    }

    return result
}
